'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { FaWhatsapp } from 'react-icons/fa6';
import { LuChevronRight, LuX } from 'react-icons/lu';
import { useWhatsApp } from '@/contexts/WhatsAppContext';
import { pushDataLayerEvent } from '@/utils/dataLayer';
import { trackMetaEvent } from '@/utils/metaPixel';

// Formato internacional obrigatório nos links wa.me (351 = Portugal).
const WHATSAPP_NUMBER = '351964791839';
const OPEN_HOUR = 9;
const CLOSE_HOUR = 19;
// 1 = segunda … 5 = sexta. Fim de semana fechado — não há ninguém na loja
// para responder ao sábado nem ao domingo.
const OPEN_WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const GENERAL_OPTIONS = [
  {
    key: 'consulta',
    label: 'Marcar uma consulta',
    message: 'Olá! Gostaria de marcar uma consulta.',
  },
  {
    key: 'encomenda',
    label: 'Dúvida sobre uma encomenda',
    message: 'Olá! Tenho uma dúvida sobre uma encomenda.',
  },
  {
    key: 'outro',
    label: 'Outro assunto',
    message: 'Olá! Preciso de ajuda.',
  },
];

/** Disponibilidade calculada na hora de Lisboa, seja qual for o fuso do cliente. */
function getAvailability(): { online: boolean; text: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Lisbon',
    weekday: 'short',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const weekday = WEEKDAY_INDEX[parts.find((p) => p.type === 'weekday')?.value ?? 'Mon'];
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);

  const openToday = OPEN_WEEKDAYS.includes(weekday);

  if (openToday && hour >= OPEN_HOUR && hour < CLOSE_HOUR) {
    return { online: true, text: 'Disponíveis agora · respondemos em minutos' };
  }
  if (openToday && hour < OPEN_HOUR) {
    return { online: false, text: `Respondemos hoje a partir das ${OPEN_HOUR}h` };
  }
  // Sexta depois das 19h, ou já fim de semana (sábado/domingo) — nestes
  // três casos a próxima abertura é sempre segunda-feira, nunca "amanhã".
  if (weekday === 5 || weekday === 6 || weekday === 0) {
    return {
      online: false,
      text: `Respondemos na segunda-feira a partir das ${OPEN_HOUR}h`,
    };
  }
  return { online: false, text: `Respondemos amanhã a partir das ${OPEN_HOUR}h` };
}

/** Altura das barras fixas no fundo do ecrã (ex: "Adicionar ao carrinho" no telemóvel). */
function getBottomBarsHeight(): number {
  let height = 0;
  document.querySelectorAll<HTMLElement>('[data-bottom-bar]').forEach((el) => {
    const style = window.getComputedStyle(el);
    if (style.position === 'fixed' && style.display !== 'none') {
      height = Math.max(height, el.offsetHeight);
    }
  });
  // Painéis muito altos (ex: opções do produto abertas) — o botão nunca sai
  // do ecrã; nesse caso fica sobre o canto do painel.
  return Math.min(height, Math.max(0, window.innerHeight - 160));
}

const WhatsAppButton = () => {
  const { topic } = useWhatsApp();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [availability, setAvailability] = useState<{
    online: boolean;
    text: string;
  } | null>(null);
  const [bottomOffset, setBottomOffset] = useState(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  // Sobe o botão por cima de barras fixas no fundo, que aparecem e mudam de
  // altura depois da navegação.
  useEffect(() => {
    const measure = () => setBottomOffset(getBottomBarsHeight());
    measure();
    const timers = [setTimeout(measure, 300), setTimeout(measure, 1200)];

    const observer = new ResizeObserver(measure);
    document
      .querySelectorAll<HTMLElement>('[data-bottom-bar]')
      .forEach((el) => observer.observe(el));
    window.addEventListener('resize', measure);

    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [pathname]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    setAvailability(getAvailability());

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const buildLink = useCallback(
    (message: string, includeUrl?: boolean) => {
      const text =
        includeUrl && typeof window !== 'undefined'
          ? `${message}\n${window.location.href}`
          : message;
      return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    },
    [],
  );

  const track = useCallback((topicKey: string) => {
    pushDataLayerEvent('generate_lead', {
      form_name: 'whatsapp',
      whatsapp_topic: topicKey,
    });
    trackMetaEvent('Contact', { content_name: `whatsapp_${topicKey}` });
  }, []);

  const options = [
    ...(topic
      ? [
          {
            key: 'pagina',
            label: topic.label,
            href: buildLink(topic.message, topic.includeUrl),
            highlight: true,
          },
        ]
      : []),
    ...GENERAL_OPTIONS.map((o) => ({
      key: o.key,
      label: o.label,
      href: buildLink(o.message),
      highlight: false,
    })),
  ];

  const bottom = `calc(${bottomOffset}px + 16px + env(safe-area-inset-bottom, 0px))`;
  // No desktop fica alinhado com a margem do conteúdo (1280px, igual ao
  // menu/footer), não encostado ao limite da janela — em ecrãs largos isso
  // deixava-o muito afastado do site. No mobile mantém-se no canto.
  // Importante: 100% e não 100vw — por definição da spec, vw inclui a
  // barra de scroll vertical, enquanto % (num elemento fixed) resolve
  // contra o viewport já sem ela. Com 100vw, sempre que a página precisa de
  // scroll vertical (ex: por a barra de contagem ter tornado a página mais
  // alta) este cálculo ficava maior do que devia, empurrando o botão para
  // fora do lado direito do ecrã — e isso por si só cria scroll horizontal.
  const right = { base: '16px', md: 'max(16px, calc((100% - 1280px) / 2))' };

  return (
    <>
      {open && (
        <Flex
          ref={panelRef}
          role="dialog"
          aria-label="Contactar por WhatsApp"
          pos="fixed"
          right={right}
          zIndex={1001}
          w="min(340px, calc(100% - 32px))"
          flexDir="column"
          bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
          borderRadius="18px"
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.08)' }}
          boxShadow="0 16px 48px rgba(0,0,0,0.18)"
          overflow="hidden"
          style={{ bottom: `calc(${bottomOffset}px + 88px + env(safe-area-inset-bottom, 0px))` }}
        >
          <Flex bg="#075E54" color="white" p="14px 16px" align="center" gap="10px">
            <Icon w="26px" h="26px">
              <FaWhatsapp />
            </Icon>
            <Flex flexDir="column" flex="1" minW="0">
              <Text fontSize="15px" fontWeight="700">
                Fale connosco no WhatsApp
              </Text>
              {availability && (
                <Flex align="center" gap="6px">
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={availability.online ? '#25D366' : '#F5C542'}
                    flexShrink={0}
                  />
                  <Text fontSize="12px" opacity={0.9}>
                    {availability.text}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Flex
              as="button"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
              p="4px"
              borderRadius="full"
              _hover={{ bg: 'rgba(255,255,255,0.15)' }}
            >
              <Icon w="18px" h="18px">
                <LuX />
              </Icon>
            </Flex>
          </Flex>

          <Flex flexDir="column" p="10px" gap="6px">
            <Text
              fontSize="12px"
              px="6px"
              pb="2px"
              color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.55)' }}
            >
              Como podemos ajudar?
            </Text>
            {options.map((option) => (
              <a
                key={option.key}
                href={option.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track(option.key);
                  setOpen(false);
                }}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  gap="10px"
                  px="12px"
                  py="10px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={
                    option.highlight
                      ? '#25D366'
                      : { base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.1)' }
                  }
                  bg={
                    option.highlight
                      ? { base: 'rgba(37,211,102,0.08)', _dark: 'rgba(37,211,102,0.12)' }
                      : 'transparent'
                  }
                  transition="all 0.15s"
                  _hover={{ borderColor: '#25D366', bg: 'rgba(37,211,102,0.08)' }}
                >
                  <Flex flexDir="column" minW="0">
                    {option.highlight && (
                      <Text fontSize="10px" fontWeight="700" color="#128C7E" textTransform="uppercase" letterSpacing="0.04em">
                        Sobre esta página
                      </Text>
                    )}
                    <Text
                      fontSize="14px"
                      fontWeight="600"
                      color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                      lineClamp={2}
                    >
                      {option.label}
                    </Text>
                  </Flex>
                  <Icon w="16px" h="16px" color="#128C7E" flexShrink={0}>
                    <LuChevronRight />
                  </Icon>
                </Flex>
              </a>
            ))}
          </Flex>
        </Flex>
      )}

      <Flex
        ref={buttonRef}
        as="button"
        aria-label="Contactar por WhatsApp"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        pos="fixed"
        right={right}
        zIndex={1001}
        w="58px"
        h="58px"
        borderRadius="full"
        bg="#25D366"
        color="white"
        align="center"
        justify="center"
        boxShadow="0 8px 24px rgba(37,211,102,0.45)"
        transition="transform 0.2s, bottom 0.2s"
        _hover={{ transform: 'scale(1.06)' }}
        style={{ bottom }}
      >
        <Icon w="30px" h="30px">
          {open ? <LuX /> : <FaWhatsapp />}
        </Icon>
      </Flex>
    </>
  );
};

export default WhatsAppButton;
